/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  AelEvents,
  getAelEventsArray,
  MilestoneEventFiresType,
  MilestoneEventFromToType,
  MilestoneEventType,
  MilestoneSendMethodType,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  DictionaryItem,
  EnumDictionary,
  getRouteInitData,
  getSingleNewSelectedEntity,
  TextDictionary,
} from '@gh/common-shared';
import { LocalizationService, TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  getControlValue,
  WaitingService,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';

import { get } from 'lodash';

import { MilestoneEventDetailsBusinessService } from '../../resources/milestone-event-details-business.service';
import {
  CostCenterAutocompleteListItem,
  EmailTemplateAutocompleteListItem,
  EventDateAutocompleteItem,
  MilestoneEventDetails,
  SmsTemplateListItem,
} from '../../resources/milestone-event-details.model';
import { MilestoneEventDetailsFormState } from './milestone-event-details-form.service';

@Component({
  standalone: false,
  selector: 'gh-milestone-event-details',
  styleUrls: ['milestone-event-details.component.scss'],
  templateUrl: 'milestone-event-details.component.html',
  viewProviders: [
    MilestoneEventDetailsFormState,
  ],
})
export class MilestoneEventDetailsComponent extends AbstractFormComponent<MilestoneEventDetails>
  implements OnInit {

  emailTemplateList: EmailTemplateAutocompleteListItem[];
  smsTemplateList: SmsTemplateListItem[];
  milestoneEvent: MilestoneEventDetails;
  milestoneEventType: TextDictionary;
  milestoneEventFiresTypes: EnumDictionary<MilestoneEventFiresType>;
  milestoneSendMethodTypes: EnumDictionary<MilestoneSendMethodType>;
  sendFromToTypes: EnumDictionary<MilestoneEventFromToType>;
  sendToTypes: EnumDictionary<MilestoneEventFromToType>;
  sendFromTypes: EnumDictionary<MilestoneEventFromToType>;
  groupSystemRoles: EnumDictionary<UserSystemRole>;
  systemRoleFromList: EnumDictionary<UserSystemRole>;
  systemRoleToList: EnumDictionary<UserSystemRole>;
  indSystemRoles: EnumDictionary<UserSystemRole>;
  templateList: EmailTemplateAutocompleteListItem[] = [];

  constructor(public form: MilestoneEventDetailsFormState,
              private route: ActivatedRoute,
              private router: AppRouter,
              private waitingService: WaitingService,
              private navigationService: BuilderNavigationService,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines,
              private dialogs: DialogService,
              private localizationService: LocalizationService,
              private translationService: TranslationService,
              private business: MilestoneEventDetailsBusinessService) {
    super();
  }

  ngOnInit(): void {
    const {
      milestoneEvent,
      milestoneEventType,
      milestoneEventFiresTypes,
      milestoneSendMethodTypes,
      emailTemplateList,
      smsTemplateList,
      sendFromTypes,
      sendToTypes,
      groupSystemRoles,
      indSystemRoles,
    } = getRouteInitData(this.route);
    this.milestoneEvent = milestoneEvent;
    this.milestoneEventType = milestoneEventType;
    this.emailTemplateList = emailTemplateList;
    this.smsTemplateList = smsTemplateList;
    this.milestoneSendMethodTypes = milestoneSendMethodTypes;
    this.milestoneEventFiresTypes = milestoneEventFiresTypes;
    this.sendFromTypes = sendFromTypes;
    this.sendToTypes = sendToTypes;
    this.groupSystemRoles = groupSystemRoles;
    this.indSystemRoles = indSystemRoles;
    this.systemRoleFromList = this.milestoneEvent.sendFrom === MilestoneEventFromToType.IndSystemRole
      ? this.indSystemRoles
      : this.groupSystemRoles;
    this.systemRoleToList = this.milestoneEvent.sendTo === MilestoneEventFromToType.IndSystemRole
      ? this.indSystemRoles
      : this.groupSystemRoles;

    if (this.milestoneEvent) {
      this.composeSendByTemplateList(this.milestoneEvent.sendMethod);
    }

    this.form.build(milestoneEvent);
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_milestone_event',
      [FormMode.EditMode]: this.milestoneEvent.eventDate
        ? `${this.translationService.get('lbl_edit')} ${this.milestoneEventType.get(this.milestoneEvent.eventDate.id)}`
        : '',
      [FormMode.ViewMode]: this.milestoneEvent.eventDate
        ? this.milestoneEventType.get(this.milestoneEvent.eventDate.id)
        : '',
    }[this.form.mode];
  }

  get formModeTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_milestone_event',
      [FormMode.EditMode]: 'lbl_edit_milestone_event',
      [FormMode.ViewMode]: 'lbl_view_milestone_event',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<MilestoneEventDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<MilestoneEventDetails>) =>
        this.business.save({
          ...state.value,
          costCenter: this.form.costCenterValue(),
        })),
      finish: ({ value }: FormFlowState<MilestoneEventDetails>) => {
        if (this.form.createMode) {
          this.router.switchTo(['/setup/milestone-events', value.milestoneEventId]);
        } else {
          this.form.build(value);
        }
      },
    });
  }

  get isSendToSystemRolesActive(): boolean {
    const sendToControlValue = getControlValue(this.form.group, 'sendTo');
    return sendToControlValue === MilestoneEventFromToType.GroupSystemRole
      || sendToControlValue === MilestoneEventFromToType.IndSystemRole;
  }

  get templateControlDisplayValue(): string {
    return get(getControlValue(this.form.group, 'template'), 'name', '');
  }

  get isEmailMethod(): boolean {
    return getControlValue(this.form.group, 'sendMethod') === MilestoneSendMethodType.Email;
  }

  onCostCenterFind(): void {
    const costCenter: CostCenterAutocompleteListItem = getControlValue(this.form.group, 'costCenter');
    this.dialogs.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      preselected: costCenter ? [costCenter.id] : void 0,
      multiselection: false,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((costCenterItem: CostCenterFinderListItem) => {
        this.form.setCostCenterValue(costCenterItem);
      });
  }

  onEventDateChange(eventDate: Maybe<EventDateAutocompleteItem>): void {
    const isCostCenterEventDate = eventDate
      ? eventDate.id === MilestoneEventType.SupervisorScheduleActivityEndDate
      || eventDate.id === MilestoneEventType.SupervisorScheduleActivityStartDate
      : false;
    this.form.updateCostCenterSchema(isCostCenterEventDate);
  }

  onTemplateChange(template: EmailTemplateAutocompleteListItem): void {
    this.form.updateTemplate(template);
  }

  loadAutocompleteEventDates($event: AutocompleteLoadMoreEvent): void {
    const stringHelper = this.localizationService.getStringL10nHelper();
    $event.load(this.milestoneEventType
      .filter(({ name }: DictionaryItem<any>) => stringHelper.includesIgnoreCase(name, $event.query.filter || ''))
      .seq());
  }

  onSendMethodChange({ value }: MatRadioChange): void {
    this.form.updateSendMethod(value);
    this.composeSendByTemplateList(value);
  }

  onSendFromChange({ value }: MatRadioChange): void {
    this.systemRoleFromList = value === MilestoneEventFromToType.IndSystemRole
      ? this.indSystemRoles
      : this.groupSystemRoles;
    this.form.updateSendFromSystemRole(void 0);
  }

  onSendWhenChange({ value }: MatRadioChange): void {
    const isBeforeOrAfter = MilestoneEventFiresType.After === value
      || MilestoneEventFiresType.Before === value;
    this.form.updateNumberOfDaysSchema(isBeforeOrAfter);
  }

  onSendToChange({ value }: MatRadioChange): void {
    this.systemRoleToList = value === MilestoneEventFromToType.IndSystemRole
      ? this.indSystemRoles
      : this.groupSystemRoles;

    const isGroupSystemRoleList = value === MilestoneEventFromToType.IndSystemRole
      || value === MilestoneEventFromToType.GroupSystemRole;
    this.form.updateSendToSystemRoleSchema(isGroupSystemRoleList);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityType: 'MILESTONE_EVENT',
      entityId: this.milestoneEvent.milestoneEventId,
      label: 'lbl_milestone_events',
      eventTypeIds: getAelEventsArray(AelEvents.MilestoneEvent),
    });
  }

  getCostCenterName(costCenter: CostCenterFinderListItem): Maybe<string> {
    return costCenter
      ? `${costCenter.code}: ${costCenter.name}`
      : void 0;
  }

  private composeSendByTemplateList(sendBy: MilestoneSendMethodType): void {
    this.templateList = sendBy === MilestoneSendMethodType.Email
      ? this.emailTemplateList
      : this.smsTemplateList;
  }
}
